import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Typography,
  Pagination,
  Autocomplete,
  CircularProgress,
  Menu,
  Grid
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import DatePickerField from '../../common/DatePickerDashboard';
import { exportEmployeeLogReportAction, getLastLoginDataAction } from '../../redux/actions/reportActions';
import { useTheme, useMediaQuery } from '@mui/material';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import NoDataFound from '../../common/NoDataFound';
import { debounce } from 'lodash';
import { searchAgentAction } from '../../redux/actions/userActions';

const LastLoginReport = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Redux state
  const employeeLoginData = useSelector(
    (state) => state?.allReportReducer?.lastLoginData || {}
  );

  const employeeLogin = employeeLoginData?.content || [];
  const [totalElements, setTotalElements] = useState(employeeLoginData?.totalElements || 0);

  const totalPages = employeeLoginData?.totalPages || 0;
  const isLoading = useSelector((state) => state?.reportReducer?.loading || false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [hasSearched, setHasSearched] = useState(false); // Track if search has been performed

  // Download menu state
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const handleDownloadClick = (event) => setDownloadAnchorEl(event.currentTarget);
  const handleDownloadClose = () => setDownloadAnchorEl(null);

  // Default values - Updated to MM-DD-YYYY format
  const defaultFromDate = null;
  const defaultToDate = null;

  // Filter states
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [fromDateInput, setFromDateInput] = useState(null);
  const [toDateInput, setToDateInput] = useState(null);
  const [agentSearchTerm, setAgentSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    fromDate: '',
    toDate: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
   const get_all_location = useSelector(
     (state) => state?.agency?.get_all_locations_list || []);

 const search_agent_list = useSelector((state) => state.user.search_agent || {});
  const agentOptions = search_agent_list.content || [];

//   const [isDownloading, setIsDownloading] = useState(false);


  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('YYYY-MM-DD HH:mm A');
  };

  // Don't load initial data automatically - wait for search
  useEffect(() => {
    // Only fetch data if search has been performed
    if (hasSearched) {
      fetchData();
    }
  }, [page, rowsPerPage]);

    const fetchData = () => {
      const requestFilters = {
        agentName: selectedAgent || '',
        location: selectedLocation === 'All Locations' ? '' : (selectedLocation || ''),
        fromDate: formData.fromDate ? dayjs(formData.fromDate).format('MM-DD-YYYY') : null,
        toDate: formData.toDate ? dayjs(formData.toDate).format('MM-DD-YYYY') : null
      };

      dispatch(getLastLoginDataAction(requestFilters, page, rowsPerPage, []));
    };

  const validateFields = () => {
    const errors = {};

    // Ensure at least one search criteria is filled
    const hasSearchCriteria =
      selectedAgent || (selectedLocation && selectedLocation !== 'All Locations') || fromDateInput || toDateInput;

    if (!hasSearchCriteria) {
      errors.general = 'Please provide at least one filter to search.';
    }

    // Check date validity
    if (fromDateInput && toDateInput && dayjs(fromDateInput).isAfter(toDateInput)) {
      errors.dateRange = 'From date cannot be after To date.';
    }

    setFieldErrors(errors);

    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (employeeLoginData?.totalElements !== undefined) {
      setTotalElements(employeeLoginData.totalElements);
    }
  }, [employeeLoginData]);

  const handleSearch = () => {
    // Mark all fields as touched
    setTouchedFields({
      agentName: true,
      location: true,
      fromDate: true,
      toDate: true
    });

    // Validate fields
    if (!validateFields()) {
      return;
    }

    setPage(0);
    setHasSearched(true);
    fetchData();
  };

    const handleAgentSearch = debounce((value) => {
      if (value?.length > 1) {
        dispatch(searchAgentAction(value));
      }
    }, 300);

  const handleReset = () => {
    // Reset all filter states to default values
    setSelectedLocation(null);
    setSelectedAgent(null);
    setFromDateInput(null);
    setToDateInput(null);
    setPage(0);
    setHasSearched(false); // Reset search state
    setTotalElements(0);

    // Null-safe formatting to avoid runtime error
    const resetFormData = {
      fromDate: defaultFromDate ? defaultFromDate.format('MM-DD-YYYY') : '',
      toDate: defaultToDate ? defaultToDate.format('MM-DD-YYYY') : '',
    };

    setFormData(resetFormData);
    setFieldErrors({});
    setTouchedFields({});
  };

  const handleChangePage = (event, newPage) => {
    const pageIndex = newPage - 1;
    setPage(pageIndex);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleDateChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    if (fieldName === 'fromDate') {
      setFromDateInput(value ? dayjs(value, 'MM-DD-YYYY') : null);
    } else if (fieldName === 'toDate') {
      setToDateInput(value ? dayjs(value, 'MM-DD-YYYY') : null);
    }

    if (fieldErrors.general || fieldErrors.dateRange) {
      setFieldErrors({});
    }
  };

  const handleBlur = (fieldName, errorMsg) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    if (errorMsg) {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: errorMsg }));
    }
  };

  const handleDownloadCSV = async() => {
     const requestFilters = {
      agentName: selectedAgent || '',
      location: selectedLocation === 'All Locations' ? '' : selectedLocation,
      fromDate: formData.fromDate ? dayjs(formData.fromDate).format('YYYY-MM-DD') : null,
      toDate: formData.toDate ? dayjs(formData.toDate).format('YYYY-MM-DD') : null
    };
     await dispatch(exportEmployeeLogReportAction(requestFilters, "csv"));
    handleDownloadClose();
  };

  const handleDownloadExcel = async() => {  
     const requestFilters = {
      agentName: selectedAgent || '',
      location: selectedLocation === 'All Locations' ? '' : selectedLocation,
      fromDate: formData.fromDate ? dayjs(formData.fromDate).format('YYYY-MM-DD') : null,
      toDate: formData.toDate ? dayjs(formData.toDate).format('YYYY-MM-DD') : null
    };
     await dispatch(exportEmployeeLogReportAction(requestFilters, "xlsx"));
    handleDownloadClose();
  };

  const handleDownloadPDF = async() => {
     const requestFilters = {
      agentName: selectedAgent || '',
      location: selectedLocation === 'All Locations' ? '' : selectedLocation,
      fromDate: formData.fromDate ? dayjs(formData.fromDate).format('YYYY-MM-DD') : null,
      toDate: formData.toDate ? dayjs(formData.toDate).format('YYYY-MM-DD') : null
    };
     await dispatch(exportEmployeeLogReportAction(requestFilters, "pdf"));
    handleDownloadClose();
  };

  return (
    <Box sx={{
      padding: isMobile ? '16px' : '24px',
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      <Box sx={{
        padding: isMobile ? '8px' : '24px',
        backgroundColor: '#ffffff',
        minHeight: '65vh',
        '@media (min-width: 1024px) and (min-height: 1366px)': {
          height: 'auto',
          minHeight: '80vh',
        },
      }}>
        {/* Header Section */}
        <Box sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Typography
            variant={isMobile ? "h6" : "h2"}
          >
            Report: Employee Log Report {hasSearched && totalElements > 0 && `(${totalElements})`}
          </Typography>
          <Box display="flex" gap={2} sx={{ mt: isMobile ? 1 : 0 }}>
            <Button
              variant="outlined"
              onClick={handleDownloadClick}
              disabled={!hasSearched || employeeLogin.length === 0}
            >
              Download
            </Button>
            <Menu
              anchorEl={downloadAnchorEl}
              open={Boolean(downloadAnchorEl)}
              onClose={handleDownloadClose}
            >
              <MenuItem onClick={handleDownloadExcel}>Excel</MenuItem>
              <MenuItem onClick={handleDownloadCSV}>CSV</MenuItem>
              <MenuItem onClick={handleDownloadPDF}>PDF</MenuItem>
            </Menu>
          </Box>
        </Box>
        {/* Filter Section */}
        <Box mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <Box sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            alignItems: 'flex-end',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            overflowX: 'auto',
            width: '100%'
          }}>
            {/* From Date */}
            <Box sx={{
              width: isMobile ? '100%' : '220px',
              flexShrink: 0
            }}>
              <label style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                From Date
              </label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePickerField
                  fieldName="fromDate"
                  formData={formData}
                  handleDateChange={handleDateChange}
                  handleBlur={handleBlur}
                  touchedFields={touchedFields}
                  fieldErrors={fieldErrors}
                  size={isMobile ? "medium" : "medium"}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      height: isMobile ? '48px' : '56px',
                      fontWeight: 'bold',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 14,
                      fontWeight: 'bold',
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>

            {/* To Date */}
            <Box sx={{
              width: isMobile ? '100%' : '220px',
              flexShrink: 0
            }}>
              <label style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>
                To Date
              </label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePickerField
                  fieldName="toDate"
                  formData={formData}
                  handleDateChange={handleDateChange}
                  handleBlur={handleBlur}
                  touchedFields={touchedFields}
                  fieldErrors={fieldErrors}
                  size={isMobile ? "medium" : "medium"}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                      height: isMobile ? '48px' : '56px',
                      fontWeight: 'bold',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: 14,
                      fontWeight: 'bold',
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>

            {/* Location Autocomplete */}
           <Grid item xs={12} sm={6} md={3}>
              <label style={{ fontSize: 14, fontWeight: 400, display: 'block', marginBottom: 4 }}>
              Select Location
              </label>
              <Autocomplete
                freeSolo
                options={get_all_location}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? option
                    : option.locationDescription || ''
                }
                renderOption={(props, option) => (
                  <li {...props}>
                    {option.locationDescription}
                  </li>
                )}
                onInputChange={(e, newInputValue) => {
                  setSelectedLocation(newInputValue);
                }}
                onChange={(e, newValue) => {
                  setSelectedLocation(newValue?.locationDescription || newValue || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size={isMobile ? 'small' : 'medium'}
                    placeholder="Select Location"
                    value={selectedLocation}
                    onChange={(e) => {
                      setSelectedLocation(e.target.value);
                    }}
                    sx={{
                      width: '220px',
                      '& .MuiInputBase-root': {
                        height: isMobile ? '40px' : '55px'
                      }
                    }}
                  />
                )}
              />
            </Grid>


            {/* Agent Autocomplete */}
          <Grid item xs={12} sm={6} md={3}>
            <label style={{ fontSize: 14, fontWeight: 400, display: 'block', marginBottom: 4 }}>
            Select Agent
            </label>
            <Autocomplete
              options={agentOptions}
              getOptionLabel={(option) =>
                typeof option === 'string'
                  ? option
                  : `${option.firstName || ''} ${option.lastName || ''}`
              }
              renderOption={(props, option) => (
                <li {...props}>
                  {option.firstName} {option.lastName}
                </li>
              )}
              onInputChange={(e, newInputValue) => {
                setAgentSearchTerm(newInputValue);
                handleAgentSearch(newInputValue);
              }}
              onChange={(e, newValue) => {
                setSelectedAgent(
                  newValue?.firstName
                    ? `${newValue.firstName} ${newValue.lastName}`
                    : newValue || ''
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size={isMobile ? 'small' : 'medium'}
                  placeholder="Search Agent"
                  value={selectedAgent}
                  onChange={(e) => {
                    setSelectedAgent(e.target.value);
                  }}
                  sx={{
                    width: '220px',
                    '& .MuiInputBase-root': {
                      height: isMobile ? '40px' : '55px'
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexShrink: 0,
              ml: 0,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleSearch}
              disabled={isLoading}
              size="large"
            >
              Search
            </Button>

            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={isLoading}
              size="large"
            >
              Reset
            </Button>
          </Box>

          </Box>

          {/* Validation Error Messages */}
          {(fieldErrors.general || fieldErrors.dateRange) && (
            <Box sx={{ mt: 2 }}>
              {fieldErrors.general && (
                <Typography variant="body2" color="error" sx={{ fontSize: '12px', mb: 1 }}>
                  {fieldErrors.general}
                </Typography>
              )}
              {fieldErrors.dateRange && (
                <Typography variant="body2" color="error" sx={{ fontSize: '12px' }}>
                  {fieldErrors.dateRange}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {!hasSearched ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            backgroundColor: '#f9f9f9',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <NoDataFound discription={"Please use the search filters above to fetch and display updated Employee Log Report data."} />
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: isMobile ? '60vh' : 'none',
                overflow: 'auto',
                width: '100%',
                marginBottom: '16px',
                boxShadow: 'none',
                border: '1px solid #e0e0e0'
              }}
            >
              <Table
                stickyHeader
                sx={{
                  minWidth: isMobile ? '650px' : '100%',
                  '& .MuiTableCell-root': {
                    fontSize: isMobile ? '12px' : '14px',
                    padding: isMobile ? '8px' : '12px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: isMobile ? '100px' : '200px'
                  }
                }}
                aria-label="employee login table"
                size={isMobile ? "small" : "medium"}
              >
                <TableHead>
                  <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px'
                    }}>Agent Name</TableCell>
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px'
                    }}>Location</TableCell>
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px'
                    }}>Last Login</TableCell>
                    <TableCell className="styled-table-heading" sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      top: 0,
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px'
                    }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={30} />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                          Loading...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : employeeLogin.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No data found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    employeeLogin.map((row, index) => (
                      <TableRow
                        key={index}
                        className="styled-table-row"
                        sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                      >
                        <TableCell>{row.agentName || 'N/A'}</TableCell>
                        <TableCell>{row.location || 'N/A'}</TableCell>
                        <TableCell>{formatDateTime(row.lastLogin)}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: row.status === 'Active' ? 'green' :
                                    row.status === 'Inactive' ? 'red' : 'inherit'
                            }}
                          >
                            {row.status || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalElements > 0 && (
              <Box sx={{
                display: "flex",
                justifyContent: { xs: "center", sm: "flex-end" },
                alignItems: "center",
                padding: { xs: "1rem 0", sm: "1rem" },
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 1 }
              }}>
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: { xs: "12px", sm: "14px" }
                }}>
                  <span style={{ marginRight: 8 }}>Rows per page:</span>
                  <select
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    style={{
                      padding: "6px 12px",
                      borderRadius: "4px",
                      borderColor: "#ccc",
                      fontSize: isMobile ? "12px" : "14px"
                    }}
                  >
                    {[5, 10, 15, 25, 50, 100].map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </Box>

                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={handleChangePage}
                  showFirstButton
                  showLastButton
                  size={isMobile ? "small" : "medium"}
                  sx={{ ml: { xs: 0, sm: 2 } }}
                  disabled={isLoading}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default LastLoginReport;